import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { KnowledgeDocumentFacet } from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Text } from '@/components/ui/Text'
import { Textarea } from '@/components/ui/textarea'
import { KnowledgeDocumentWithStages } from '@/lib/api'
import {
  documentTitle,
  fileKindLabel,
  formatBytes,
  formatLibraryDate,
  formatLibraryDateTime,
  parseFindingsItems,
} from '../KnowledgeHubPage/libraryHelpers'
import { describePreviewKind } from './previewKind'
import { TagsCard } from './TagsCard'

interface InfoRowProps {
  label: string
  value: string
  testId: string
}

function InfoRow(props: InfoRowProps) {
  const { label, value, testId } = props

  return (
    <>
      <Text size="xs" color="muted">
        {label}
      </Text>
      <Text size="sm" weight="medium" className="break-words" data-testid={testId}>
        {value}
      </Text>
    </>
  )
}

export interface DetailsPanelProps {
  document: KnowledgeDocumentWithStages
  /** A title/summary save is in flight. */
  savingDetails: boolean
  onSaveTitle: (title: string) => void
  onSaveSummary: (summary: string) => void
  facetBusy: boolean
  onConfirmFacet: (facet: KnowledgeDocumentFacet) => void
  onRejectFacet: (facet: KnowledgeDocumentFacet) => void
  onCorrectFacet: (facet: KnowledgeDocumentFacet, value: string) => void
  onConfirmAllFacets: () => void
  instructionsDraft: string
  instructionsBusy: boolean
  onInstructionsChange: (value: string) => void
  onSaveInstructions: () => void
  className?: string
  linkToLibrary?: boolean
  editConfirmedTags?: boolean
}

/**
 * Right column of the document page. Section order is fixed by product:
 * Name → Description → Document Data → Tags → Instructions → Used in.
 */
export function DetailsPanel(props: DetailsPanelProps) {
  const {
    document,
    savingDetails,
    onSaveTitle,
    onSaveSummary,
    facetBusy,
    onConfirmFacet,
    onRejectFacet,
    onCorrectFacet,
    onConfirmAllFacets,
    instructionsDraft,
    instructionsBusy,
    onInstructionsChange,
    onSaveInstructions,
    className,
    linkToLibrary = true,
    editConfirmedTags = false,
  } = props
  const { t } = useTranslation('app')

  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState('')
  const [instructionsOpen, setInstructionsOpen] = useState(false)
  const [methodologyOpen, setMethodologyOpen] = useState(false)

  const title = documentTitle(document)
  const facets = document.facets ?? []
  const findingsItems = useMemo(
    () => parseFindingsItems(document.findingsMarkdown),
    [document.findingsMarkdown],
  )

  useEffect(() => {
    setEditingName(false)
    setEditingDescription(false)
  }, [document.token])

  function startEditingName() {
    setNameDraft(title)
    setEditingName(true)
  }

  function commitName() {
    const next = nameDraft.trim()
    setEditingName(false)
    if (!next || next === title) {
      return
    }
    onSaveTitle(next)
  }

  function startEditingDescription() {
    setDescriptionDraft(document.summary ?? '')
    setEditingDescription(true)
  }

  function commitDescription() {
    const next = descriptionDraft.trim()
    setEditingDescription(false)
    if (next === (document.summary ?? '').trim()) {
      return
    }
    onSaveSummary(next)
  }

  // Reuse the preview pane's own classification so the "Pages" row and the
  // renderer can never disagree about what kind of document this is. Videos,
  // audio, links, and HTML uploads aren't paged at all (see isPagedDocument),
  // so a stored pageCount for them (transcript segments, sheet count, …) would
  // be misleading and the row is hidden entirely. Paged documents get a label
  // that matches their actual unit: slides, sheets, or pages.
  const previewKind = describePreviewKind({
    mimeType: document.mimeType,
    originalFilename: document.originalFilename,
    sourceUrl: document.sourceUrl,
  })
  const showPageCount = previewKind.isPagedDocument
  const pageCountLabel = previewKind.isSlideUpload
    ? t('library.preview.slides')
    : previewKind.tabularFormat?.unitNoun === 'sheet'
      ? t('library.preview.sheets')
      : t('library.preview.pages')
  const pagesValue =
    document.pageCount != null && document.pageCount > 0
      ? String(document.pageCount)
      : '—'

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden bg-background border-t border-border lg:border-t-0 lg:border-l',
        className,
      )}
      data-testid="knowledge-doc-details-panel"
    >
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-5">
        <section className="space-y-2" data-testid="knowledge-doc-section-name">
          {editingName ? (
            <div className="space-y-2">
              <Input
                variant="featured"
                value={nameDraft}
                autoFocus
                onChange={(event) => setNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    commitName()
                  }
                  if (event.key === 'Escape') {
                    setEditingName(false)
                  }
                }}
                className="text-2xl"
                data-testid="knowledge-doc-name-input"
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={savingDetails}
                  onClick={commitName}
                  data-testid="knowledge-doc-name-save"
                >
                  {savingDetails ? t('library.saving') : t('library.save')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingName(false)}
                  data-testid="knowledge-doc-name-cancel"
                >
                  {t('library.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <Heading
                level={1}
                size="2xl"
                className="min-w-0 flex-1 break-words leading-tight"
                data-testid="knowledge-doc-title"
              >
                {title}
              </Heading>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                aria-label={t('library.preview.editName')}
                onClick={startEditingName}
                data-testid="knowledge-doc-name-edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </section>

        <section
          className="space-y-2"
          data-testid="knowledge-doc-section-description"
        >
          <div className="flex items-center justify-between gap-2">
            <Text size="sm" className="font-medium">
              {t('library.preview.description')}
            </Text>
            {editingDescription ? null : (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                aria-label={t('library.preview.editDescription')}
                onClick={startEditingDescription}
                data-testid="knowledge-doc-description-edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          {editingDescription ? (
            <div className="space-y-2">
              <Textarea
                value={descriptionDraft}
                autoFocus
                onChange={(event) => setDescriptionDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setEditingDescription(false)
                  }
                }}
                placeholder={t('library.preview.descriptionPlaceholder')}
                className="min-h-[96px] text-sm"
                data-testid="knowledge-doc-description-input"
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={savingDetails}
                  onClick={commitDescription}
                  data-testid="knowledge-doc-description-save"
                >
                  {savingDetails ? t('library.saving') : t('library.save')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingDescription(false)}
                  data-testid="knowledge-doc-description-cancel"
                >
                  {t('library.cancel')}
                </Button>
              </div>
            </div>
          ) : document.summary ? (
            <Text
              size="sm"
              className="leading-relaxed"
              data-testid="knowledge-doc-summary"
            >
              {document.summary}
            </Text>
          ) : document.findingsMarkdown ? (
            <div data-testid="knowledge-doc-findings-lead">
              {findingsItems.length >= 2 ? (
                <ol
                  className="list-decimal space-y-2 pl-5 text-sm"
                  data-testid="knowledge-doc-findings-list"
                >
                  {findingsItems.map((item, i) => (
                    <li key={`${i}-${item.slice(0, 24)}`}>{item}</li>
                  ))}
                </ol>
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
                  {document.findingsMarkdown}
                </pre>
              )}
            </div>
          ) : (
            <Text size="sm" color="muted" data-testid="knowledge-doc-description-empty">
              {t('library.preview.noDescription')}
            </Text>
          )}
        </section>

        <Separator />

        <section
          className="space-y-2"
          data-testid="knowledge-doc-section-document-data"
        >
          <Text size="sm" className="font-medium">
            {t('library.preview.documentData')}
          </Text>
          <div className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-3 gap-y-2">
            <InfoRow
              label={t('library.preview.type')}
              value={fileKindLabel(document) || '—'}
              testId="knowledge-doc-data-type"
            />
            <InfoRow
              label={t('library.preview.size')}
              value={
                document.fileSize != null ? formatBytes(document.fileSize) : '—'
              }
              testId="knowledge-doc-data-size"
            />
            {showPageCount ? (
              <InfoRow
                label={pageCountLabel}
                value={pagesValue}
                testId="knowledge-doc-data-pages"
              />
            ) : null}
            <InfoRow
              label={t('library.preview.owner')}
              value={document.ownerName || '—'}
              testId="knowledge-doc-data-owner"
            />
            <InfoRow
              label={t('library.preview.uploaded')}
              value={formatLibraryDate(document.createdAt)}
              testId="knowledge-doc-data-uploaded"
            />
          </div>
        </section>

        <Separator />

        <section className="space-y-2" data-testid="knowledge-doc-section-tags">
          <Text size="sm" className="font-medium">
            {t('library.preview.tags')}
          </Text>
          <TagsCard
            facets={facets}
            ownerName={document.ownerName}
            busy={facetBusy}
            onConfirmFacet={onConfirmFacet}
            onRejectFacet={onRejectFacet}
            onCorrectFacet={onCorrectFacet}
            onConfirmAllFacets={onConfirmAllFacets}
            linkToLibrary={linkToLibrary}
            editConfirmed={editConfirmedTags}
          />
        </section>

        <Separator />

        <section
          className="space-y-2"
          data-testid="knowledge-doc-section-instructions"
        >
          <button
            type="button"
            className="flex w-full items-center justify-between text-left"
            onClick={() => setInstructionsOpen((v) => !v)}
            data-testid="knowledge-doc-instructions-toggle"
          >
            <Text size="sm" className="font-medium">
              {t('library.preview.instructions')}
            </Text>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                instructionsOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {!instructionsOpen ? (
            <Text size="sm" color="muted">
              {t('library.preview.instructionsHint')}
            </Text>
          ) : (
            <>
              <Textarea
                className="min-h-[88px] text-sm"
                value={instructionsDraft}
                onChange={(event) => onInstructionsChange(event.target.value)}
                placeholder={t('library.preview.instructionsHint')}
                data-testid="knowledge-doc-instructions-input"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                shape="pill"
                disabled={instructionsBusy}
                onClick={onSaveInstructions}
                data-testid="knowledge-doc-instructions-save"
              >
                {instructionsBusy ? t('library.saving') : t('library.preview.saveInstructions')}
              </Button>
            </>
          )}
        </section>

        <Separator />

        <section
          className="space-y-2"
          data-testid="knowledge-doc-section-used-in"
        >
          <Text size="sm" className="font-medium">
            {t('library.preview.usedIn')}
          </Text>
          {(document.usedIn?.length ?? 0) === 0 ? (
            <Text size="sm" color="muted">
              {t('library.preview.notCited')}
            </Text>
          ) : (
            <ul className="space-y-1 text-sm">
              {document.usedIn!.map((u) => (
                <li
                  key={`${u.studyId}-${u.populationId}`}
                  className="rounded-lg border border-card-border px-3 py-2"
                  data-testid="knowledge-doc-used-in-row"
                >
                  {t('library.preview.studyId', { id: u.studyId })}
                  <Text size="xs" color="muted" className="mt-0.5">
                    {t('library.preview.activated', {
                      when: formatLibraryDateTime(u.activatedAt),
                    })}
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </section>

        {document.methodologyMarkdown ? (
          <div>
            <button
              type="button"
              className="text-sm text-muted-foreground underline hover:text-foreground"
              data-testid="knowledge-methodology-toggle"
              onClick={() => setMethodologyOpen((v) => !v)}
            >
              {methodologyOpen
                ? t('library.preview.hideMethodology')
                : t('library.preview.showMethodology')}
            </button>
            {methodologyOpen ? (
              <pre
                className="mt-2 whitespace-pre-wrap font-sans text-sm text-foreground"
                data-testid="knowledge-doc-methodology"
              >
                {document.methodologyMarkdown}
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
